import { useAuth } from '@app/icare/client/auth'
import { db } from '@app/icare/client/db'
import type { ResultTypeFromQueryResult } from '@app/icare/client/db/client/localQueryOptions'
import { localPaginatedQueryOptions } from '@app/icare/client/db/client/localQueryOptions'
import { relation } from '@app/icare/client/db/helpers'
import { TherapyStatusBadge } from '@app/icare/components/status/TherapyStatusBadge'
import { patientRelations } from '@app/icare/db/query/patientRelations'
import { queryKit } from '@app/icare/db/query/queryKit'
import PatientSummary from '@app/icare/entities/patient/components/PatientSummary'
import { PatientStatusEnum, type PatientWithRelations } from '@app/icare/entities/patient/schema/Patient'
import type { PatientTherapyRelation } from '@app/icare/entities/patient/schema/PatientTherapy'
import { type TherapyType, TherapyTypeEnum } from '@app/icare/entities/therapy/schema/TherapyType'
import { mapPatientTherapyToTherapyStatus, TherapyStatusEnum } from '@app/icare/shared/api/therapy.type'
import { Trans } from '@lingui/react/macro'
import { createQueryStore, F, QueryBaseSchema, useColumnDef } from '@pkg/api/query'
import { ButtonLink, FilterButton } from '@pkg/ui/button'
import { LinkIndicatorIcon, SearchInput } from '@pkg/ui/common'
import { FormattedPatientNameAndAddress } from '@pkg/ui/formattedContent'
import { usePageContainerOverride } from '@pkg/ui/layout/PageContainer'
import { QueryFilterWrapper } from '@pkg/ui/query/QueryFilterWrapper'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Space, Table } from 'antd'
import { sql } from 'kysely'
import { useState } from 'react'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'

export const Route = createFileRoute('/_app/_dashboards/registered-patients')({
  component: RouteComponent,
})

const FilterSchema = QueryBaseSchema.extend({
  filters: z
    .object({
      'patient.status.value': z.array(z.enum(PatientStatusEnum.$.options.map(o => o.value))).nullish(),
      'therapies.type': z.array(z.enum(TherapyTypeEnum.$.options.map(o => o.value))).nullish(),
    })
    .optional(),
  sortBy: z.enum(['patient.personal.lastName']).default('patient.personal.lastName').nullish(),
})

const INITIAL_FILTERS = {
  'patient.status.value': F.in([PatientStatusEnum.new]),
}
const useRegisteredPatientsQueryStore = createQueryStore({
  schema: FilterSchema,
  initialState: {
    filters: INITIAL_FILTERS,
  },
})

function RouteComponent() {
  const { current } = useAuth()
  const queryStore = useRegisteredPatientsQueryStore(useShallow(s => s))
  const [open, setOpen] = useState(false)

  const registeredPatientsParams = queryKit.parseQuery(queryStore, { paginationMode: 'offset' })
  const therapyTypeFilter = registeredPatientsParams.filters.find(f => f.field === 'therapies.type')
  const therapyTypeValues: TherapyType[] =
    therapyTypeFilter?.operator === 'in' && Array.isArray(therapyTypeFilter.value)
      ? therapyTypeFilter.value.flatMap(value => (TherapyTypeEnum.$.is(value) ? [value] : []))
      : therapyTypeFilter?.operator === 'eq' && TherapyTypeEnum.$.is(therapyTypeFilter.value)
        ? [therapyTypeFilter.value]
        : []

  const registeredPatientsBaseParams = {
    ...registeredPatientsParams,
    filters: registeredPatientsParams.filters.filter(f => f.field !== 'therapies.type'),
  }

  const { data, isFetching } = useQuery(
    localPaginatedQueryOptions({
      query: db
        .selectFrom('patient')
        .selectAll('patient')
        .$if(!!current?.id, qb =>
          qb.where(({ exists, selectFrom }) =>
            exists(
              selectFrom('patientCareManager')
                .select('patientCareManager.id')
                .whereRef('patientCareManager.patientId', '=', 'patient.id')
                .$call(relation.isActive('patientCareManager'))
                .where(sql<boolean>`${sql.ref('patientCareManager.toId')} = ${current?.id}`),
            ),
          ),
        )
        .$if(therapyTypeValues.length > 0, qb =>
          qb.where(({ exists, selectFrom }) =>
            exists(
              selectFrom('patientTherapy')
                .select('patientTherapy.id')
                .whereRef('patientTherapy.patientId', '=', 'patient.id')
                .$call(relation.isActive('patientTherapy'))
                .where('patientTherapy.type', 'in', therapyTypeValues),
            ),
          ),
        )
        //.where('status', '=', PatientStatusEnum.new)
        .select([patientRelations.therapies(), patientRelations.careManagers(), patientRelations.adTeams()])
        .$call(queryKit.all(registeredPatientsBaseParams)),
      params: registeredPatientsBaseParams,
    }),
  )

  const columns = useColumnDef<ResultTypeFromQueryResult<typeof data>>(
    () => [
      {
        key: 'personal.lastName',
        title: <Trans>Patientenakte</Trans>,
        fixed: 'start',
        sorter: true,
        render: (_, record) => <FormattedPatientNameAndAddress patient={record} />,
      },
      {
        key: 'patient.therapies',
        title: <Trans>Therapien</Trans>,
        sorter: false,
        render: (_, record) => (
          <div className="flex flex-wrap gap-2">
            {(record.therapies || [])
              .filter(
                (therapy: PatientTherapyRelation) =>
                  mapPatientTherapyToTherapyStatus(therapy, record.deceased) !== TherapyStatusEnum.ENDED,
              )
              .map((therapy: PatientTherapyRelation) => (
                <div key={therapy.id}>
                  <TherapyStatusBadge
                    className="mx-0"
                    value={mapPatientTherapyToTherapyStatus(therapy, record.deceased)}
                  >
                    {TherapyTypeEnum.$.getLabel(therapy.type)}
                  </TherapyStatusBadge>
                </div>
              ))}
          </div>
        ),
      },
      {
        key: 'actions',
        title: <Trans>Aktionen</Trans>,
        fixed: 'end',
        sorter: false,
        render: (_, record) => (
          <ButtonLink to="/patienten/$id" params={{ id: record.id }} size="small">
            <Trans>zur Akte</Trans>
            <LinkIndicatorIcon />
          </ButtonLink>
        ),
      },
    ],
    [],
    useRegisteredPatientsQueryStore,
  )

  usePageContainerOverride(
    {
      header: {
        extra: (
          <Space size="small">
            <SearchInput
              size="small"
              value={queryStore.meta.searchRawValue}
              onChange={e => queryStore.setSearch(e.target.value)}
            />
            <FilterButton activeFilterCount={queryStore.meta.activeFilterCount} onClick={() => setOpen(true)} />
          </Space>
        ),
      },
    },
    [queryStore.meta.searchRawValue, queryStore.setSearch, queryStore.meta.activeFilterCount, setOpen, queryStore],
  )

  return (
    <QueryFilterWrapper
      queryStore={useRegisteredPatientsQueryStore}
      open={open}
      onOpenChange={setOpen}
      filterConfig={[
        {
          name: 'patient.status.value',
          mode: 'multiple',
          label: <Trans>Status der Patientenakte</Trans>,
          options: PatientStatusEnum.$.options,
        },
        {
          name: 'therapies.type',
          mode: 'multiple',
          label: <Trans>Therapieform</Trans>,
          options: TherapyTypeEnum.$.options,
        },
      ]}
    >
      <Table
        dataSource={data?.data ?? []}
        loading={isFetching}
        columns={columns}
        rowKey="id"
        size="small"
        pagination={data}
        onChange={queryStore.onChange}
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowRender: record => <PatientSummary patient={record as PatientWithRelations} />,
        }}
      />
    </QueryFilterWrapper>
  )
}
